import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { PageTransition } from "@/components/PageTransition";
import { BackToTop } from "@/components/BackToTop";
import { SkipLink } from "@/components/SkipLink";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import Excursions from "./pages/Excursions";
import ExcursionDetail from "./pages/ExcursionDetail";
import Contact from "./pages/Contact";
import About from "./pages/About";
import Avis from "./pages/Avis";
import MentionsLegales from "./pages/MentionsLegales";
import PolitiqueConfidentialite from "./pages/PolitiqueConfidentialite";
import NotFound from "./pages/NotFound";
import { CookieBanner } from "./components/CookieBanner";
import { ScrollToTop } from "./components/ScrollToTop";
import { useGTM } from "./hooks/useGTM";
import { useClarity } from "./hooks/useClarity";

// Lazy load admin pages to reduce initial bundle size
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const AdminExcursions = lazy(() => import("./pages/AdminExcursions"));
const AdminExcursionEdit = lazy(() => import("./pages/AdminExcursionEdit"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));

const queryClient = new QueryClient();

const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center">Chargement...</div>
);

const AppContent = () => {
  useGTM();
  useClarity();

  return (
    <>
      <SkipLink />
      <ScrollToTop />
      <BackToTop />
      <Routes>
        <Route path="/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/excursions" element={<PageTransition><Excursions /></PageTransition>} />
        <Route path="/excursions/:id" element={<PageTransition><ExcursionDetail /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/a-propos" element={<PageTransition><About /></PageTransition>} />
        <Route path="/avis" element={<PageTransition><Avis /></PageTransition>} />
        <Route path="/admin-login" element={<PageTransition><Suspense fallback={<AdminFallback />}><AdminLogin /></Suspense></PageTransition>} />
        <Route path="/admin" element={<PageTransition><Suspense fallback={<AdminFallback />}><Admin /></Suspense></PageTransition>} />
        <Route path="/admin/testimonials" element={<PageTransition><Suspense fallback={<AdminFallback />}><AdminTestimonials /></Suspense></PageTransition>} />
        <Route path="/admin/excursions" element={<PageTransition><Suspense fallback={<AdminFallback />}><AdminExcursions /></Suspense></PageTransition>} />
        <Route path="/admin/excursions/new" element={<PageTransition><Suspense fallback={<AdminFallback />}><AdminExcursionEdit /></Suspense></PageTransition>} />
        <Route path="/admin/excursions/:id/edit" element={<PageTransition><Suspense fallback={<AdminFallback />}><AdminExcursionEdit /></Suspense></PageTransition>} />
        <Route path="/admin/users" element={<PageTransition><Suspense fallback={<AdminFallback />}><AdminUsers /></Suspense></PageTransition>} />
        <Route path="/mentions-legales" element={<PageTransition><MentionsLegales /></PageTransition>} />
        <Route path="/confidentialite" element={<PageTransition><PolitiqueConfidentialite /></PageTransition>} />
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <Toaster />
      <Sonner />
      <CookieBanner />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
